/**
 * A thin progress bar across the top of the screen while the cart is working.
 *
 * Adding to the cart can take a couple of seconds on this shop, and until it
 * finishes nothing on screen says so -- the button sits there and people tap
 * it again. This is the "something is happening" signal.
 *
 * It watches the network rather than the theme's own events: every cart
 * operation goes through fetch() to /cart/add, /cart/change or /cart/update,
 * whoever starts it -- the product form, the quantity stepper, quick add, or
 * an app. Wrapping fetch catches all of them and needs no event names, which
 * come from a Shopify platform module this theme cannot read at build time.
 *
 * The bar is deliberately indeterminate. We do not know how long the request
 * will take, so it eases towards 90% and waits there; when the request
 * settles it runs to 100% and fades. A bar that claims a percentage it cannot
 * know is worse than one that admits it is guessing.
 */
(() => {
  const CART_PATHS = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/;
  const BAR_ID = 'cart-progress';

  let inFlight = 0;
  let easeTimer = null;
  let hideTimer = null;
  let width = 0;

  const bar = () => document.getElementById(BAR_ID)?.firstElementChild;

  function set(pct) {
    const el = bar();
    if (!el) return;
    width = pct;
    el.style.transform = `scaleX(${pct / 100})`;
  }

  function start() {
    const host = document.getElementById(BAR_ID);
    if (!host) return;
    clearTimeout(hideTimer);
    host.classList.add('is-active');
    host.removeAttribute('hidden');
    set(0);
    // A quick jump, then progressively smaller steps: movement reads as
    // progress even though the number is invented.
    requestAnimationFrame(() => set(18));
    clearInterval(easeTimer);
    easeTimer = setInterval(() => {
      if (width >= 90) return;
      set(width + Math.max(0.6, (90 - width) / 12));
    }, 180);
  }

  function done() {
    const host = document.getElementById(BAR_ID);
    if (!host) return;
    clearInterval(easeTimer);
    set(100);
    hideTimer = setTimeout(() => {
      host.classList.remove('is-active');
      // Reset with the transition off, so it does not slide back visibly.
      const el = bar();
      if (el) {
        el.style.transition = 'none';
        set(0);
        requestAnimationFrame(() => {
          el.style.transition = '';
        });
      }
    }, 280);
  }

  const originalFetch = window.fetch;
  if (typeof originalFetch !== 'function') return;

  window.fetch = function (...args) {
    let url = '';
    try {
      const input = args[0];
      url = typeof input === 'string' ? input : input?.url || '';
    } catch (e) {
      url = '';
    }

    if (!CART_PATHS.test(url)) return originalFetch.apply(this, args);

    inFlight += 1;
    if (inFlight === 1) start();

    const settle = () => {
      inFlight = Math.max(0, inFlight - 1);
      if (inFlight === 0) done();
    };

    return originalFetch.apply(this, args).then(
      (response) => {
        settle();
        return response;
      },
      (error) => {
        settle();
        throw error;
      }
    );
  };
})();
